import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

// Unsent composer text, keyed by patient. Mounted above the router because both
// patient-tab navigation and leaving the chart unmount the Mai panel, which
// would otherwise discard whatever the clinician had typed.
//
// In-memory by design: a draft can carry PHI, so it must not reach browser
// storage, and it is expected to die on refresh and logout.
export type MaiChatDrafts = {
  drafts: Record<string, string>;
  setDraft: (patientId: string, draft: string) => void;
};

const MaiChatDraftsContext = createContext<MaiChatDrafts | null>(null);

export const MaiChatDraftsProvider = ({ children }: PropsWithChildren) => {
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  // An emptied draft drops its key so "never typed" and "cleared" behave alike
  // and a long session doesn't accumulate one entry per chart visited.
  const setDraft = useCallback((patientId: string, draft: string) => {
    setDrafts((prev) => {
      if (draft) return { ...prev, [patientId]: draft };
      if (!(patientId in prev)) return prev;
      const next = { ...prev };
      delete next[patientId];
      return next;
    });
  }, []);

  const value = useMemo(() => ({ drafts, setDraft }), [drafts, setDraft]);

  return (
    <MaiChatDraftsContext.Provider value={value}>
      {children}
    </MaiChatDraftsContext.Provider>
  );
};

export const useMaiChatDrafts = (): MaiChatDrafts => {
  const context = useContext(MaiChatDraftsContext);
  if (!context) {
    throw new Error(
      'useMaiChatDrafts must be used within a MaiChatDraftsProvider.',
    );
  }
  return context;
};
